import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import '../styles/toolbox.css';

// Number formatter that picks reasonable digits for the magnitude of the number.
function formatValue(v, unit) {
  if (v == null || !isFinite(v)) return '—';
  const abs = Math.abs(v);
  let digits = 2;
  if (abs >= 10000) digits = 0;
  else if (abs >= 100) digits = 2;
  else if (abs >= 10) digits = 2;
  else digits = 3;
  return v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function formatChange(pct) {
  if (pct == null || !isFinite(pct)) return '—';
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct.toFixed(2)}%`;
}

function changeClass(pct) {
  if (pct == null || !isFinite(pct)) return 'terminal-flat';
  if (pct > 0.05) return 'terminal-up';
  if (pct < -0.05) return 'terminal-down';
  return 'terminal-flat';
}

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function formatTooltipDate(isoDate) {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-');
  const MONTHS = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
  return `${MONTHS[parseInt(m, 10) - 1]} ${parseInt(d, 10)} ${y}`;
}

// Inline SVG trend chart — values/dates from SQL (real). Hover for a crosshair +
// tooltip pinned to the nearest data point showing its date and value.
function Sparkline({ points, unit }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  if (!points || points.length < 2) return <div className="terminal-sparkline-empty">INSUFFICIENT DATA</div>;
  const values = points.map(p => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = (max - min) || 1;
  const w = 260, h = 40;
  const xy = points.map((p, i) => {
    const x = (i / (points.length - 1)) * w;
    const y = h - ((p.value - min) / range) * (h - 4) - 2;
    return { x, y };
  });
  const polyPts = xy.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const first = values[0], last = values[values.length - 1];
  const stroke = last > first ? '#00FF41' : last < first ? '#FF3030' : '#888';
  const endXY = xy[xy.length - 1];

  function onMove(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    const idx = Math.max(0, Math.min(points.length - 1, Math.round(ratio * (points.length - 1))));
    setHoverIdx(idx);
  }

  const hover = hoverIdx != null ? { point: points[hoverIdx], xy: xy[hoverIdx] } : null;

  // Readout row below the chart — always shows the latest point by default so the
  // space is never wasted; during hover, swaps to the hovered point. Avoids
  // covering the trend line.
  const readoutPoint = hover ? hover.point : points[points.length - 1];
  const readoutLabel = hover ? 'HOVER' : 'LATEST';

  return (
    <div className="terminal-sparkline-wrap">
      <svg className="terminal-sparkline" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none"
           onMouseMove={onMove}
           onMouseLeave={() => setHoverIdx(null)}>
        <polyline points={polyPts} fill="none" stroke={stroke} strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
        {hover ? (
          <>
            <line x1={hover.xy.x} x2={hover.xy.x} y1="0" y2={h} stroke="#FFB000" strokeWidth="0.6" strokeDasharray="2 2" vectorEffect="non-scaling-stroke" />
            <circle cx={hover.xy.x} cy={hover.xy.y} r="2.5" fill="#FFB000" />
          </>
        ) : (
          <circle cx={endXY.x} cy={endXY.y} r="2" fill={stroke} />
        )}
      </svg>
      <div className="terminal-sparkline-readout">
        <span className="tt-label">{readoutLabel}</span>
        <span className="tt-date">{formatTooltipDate(readoutPoint.date)}</span>
        <span className="tt-value">{formatValue(readoutPoint.value)}{unit ? ` ${unit}` : ''}</span>
      </div>
    </div>
  );
}

const CATEGORY_TABS = ['ALL', 'MATERIALS', 'ENERGY', 'RATES', 'LABOR', 'SPENDING', 'ACTIVITY'];

// Single card — factored out so the Watchlist + All Series sections share identical rendering.
function TerminalCard({ s, pinned, onTogglePin, onOpen }) {
  const up = (s.percentChange ?? 0) >= 0;
  const cls = changeClass(s.percentChange);
  return (
    <div className="terminal-card" onClick={() => onOpen(s.key)} role="button" tabIndex={0}>
      <div className="terminal-card-head">
        <span className="terminal-card-sym" title={`FRED series ID: ${s.key}\n${s.description || ''}`}>{s.key}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="terminal-card-tag" title={`Category: ${s.category}`}>{s.category?.toUpperCase() || '—'}</span>
          <button
            className={`terminal-card-pin ${pinned ? 'pinned' : ''}`}
            onClick={(e) => { e.stopPropagation(); onTogglePin(s.key); }}
            title={pinned ? 'Remove from watchlist' : 'Pin to watchlist'}
          >{pinned ? '★' : '☆'}</button>
        </div>
      </div>
      <div className="terminal-card-name" title={s.description || s.displayName}>{s.displayName}</div>
      <div className="terminal-card-region" title={`Geographic coverage: ${s.region}`}>
        <span className="terminal-card-region-icon">◉</span> {s.region?.toUpperCase()}
      </div>
      <div className="terminal-card-price-row">
        <span className="terminal-card-price" title={`Most recent observation from the source, measured in ${s.unit || 'n/a'}.`}>
          {formatValue(s.currentValue, s.unit)}
        </span>
        <span className={`terminal-card-chg ${cls}`} title={`Projected change over next 6 months from ${formatValue(s.currentValue)} to ${formatValue(s.forecastValue)} (${formatChange(s.percentChange)}). Based on linear regression over last 24 months.`}>
          {up ? '▲' : '▼'} {formatChange(s.percentChange)}
        </span>
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        <Sparkline points={s.sparkline} unit={s.unit} />
      </div>
      <div className="terminal-card-foot">
        <div className="terminal-card-foot-row" title={`Unit of measurement: ${s.unit || 'n/a'}`}><span>UNIT</span><b>{(s.unit || '—').toUpperCase()}</b></div>
        <div className="terminal-card-foot-row" title={`6-month projection based on 24-month linear regression`}><span>6MO</span><b>{formatValue(s.forecastValue)}</b></div>
        <div className="terminal-card-foot-row" title={`R² — 1.00 = perfect line, 0.00 = no pattern. Low R² means unreliable projection.`}><span>R²</span><b>{s.trendR2 != null ? s.trendR2.toFixed(2) : '—'}</b></div>
        <div className="terminal-card-foot-row" title={`N — monthly data points in the regression (last 24 months)`}><span>N</span><b>{s.observationCount}</b></div>
      </div>
    </div>
  );
}

// Detail modal — full 5yr chart, observations table, description, and "Explain this move" AI button.
function DetailModal({ seriesKey, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [explaining, setExplaining] = useState(false);
  const [explanation, setExplanation] = useState(null);

  useEffect(() => {
    if (!seriesKey) return;
    setLoading(true); setDetail(null); setExplanation(null);
    fetch(`/api/toolbox/series/${encodeURIComponent(seriesKey)}`, { credentials: 'same-origin' })
      .then(r => r.json())
      .then(d => { setDetail(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [seriesKey]);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function explain() {
    if (!detail) return;
    setExplaining(true);
    try {
      const recent = detail.observations.slice(-12).map(o => `${o.date.slice(0, 7)}: ${o.value}`).join(', ');
      const question = `Brief: what could explain recent movement in ${detail.displayName} (FRED ${detail.key}, ${detail.region})? Current value: ${detail.currentValue?.toFixed(2)} ${detail.unit}. 6mo projected change: ${detail.percentChange?.toFixed(2)}%. Last 12 monthly values: ${recent}. Give 2-3 sentences on likely macro or industry drivers. Don't invent specific news events.`;
      const res = await fetch('/api/chat', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin', body: JSON.stringify({ message: question }),
      });
      const d = await res.json();
      setExplanation(d.answer || d.error || 'No response.');
    } catch (err) { setExplanation('Error: ' + err.message); }
    setExplaining(false);
  }

  if (!seriesKey) return null;
  return (
    <div className="terminal-modal-overlay" onClick={onClose}>
      <div className="terminal-modal" onClick={e => e.stopPropagation()}>
        <div className="terminal-modal-header">
          <div>
            <div className="terminal-modal-key">{seriesKey}</div>
            {detail && <div className="terminal-modal-name">{detail.displayName} &middot; {detail.region}</div>}
          </div>
          <button className="terminal-modal-close" onClick={onClose} title="Close (Esc)">×</button>
        </div>
        {loading ? (
          <div className="terminal-loading">:: LOADING FULL HISTORY ::</div>
        ) : !detail ? (
          <div className="terminal-empty">NO DATA</div>
        ) : (
          <div className="terminal-modal-body">
            <div className="terminal-modal-stats">
              <div><span className="tt-label">CURRENT</span><b>{formatValue(detail.currentValue)}</b><em>{detail.unit}</em></div>
              <div><span className="tt-label">6MO</span><b>{formatValue(detail.forecastValue)}</b><em className={changeClass(detail.percentChange)}>{formatChange(detail.percentChange)}</em></div>
              <div><span className="tt-label">R²</span><b>{detail.trendR2?.toFixed(2)}</b></div>
              <div><span className="tt-label">OBS</span><b>{detail.observations.length}</b></div>
              <div><span className="tt-label">SOURCE</span><b>{detail.source}</b></div>
            </div>

            <div className="terminal-modal-chart">
              <BigChart points={detail.observations} unit={detail.unit} />
            </div>

            <div className="terminal-modal-desc" title="Full description of the series from the data source.">
              {detail.description || '—'}
            </div>

            <div className="terminal-modal-explain">
              <button className="terminal-refresh-btn" onClick={explain} disabled={explaining}>
                {explaining ? 'ASKING...' : '✦ EXPLAIN THIS MOVE'}
              </button>
              {explanation && <div className="terminal-modal-explanation">{explanation}</div>}
            </div>

            <div className="terminal-modal-table-head">&raquo; LAST 24 OBSERVATIONS</div>
            <div className="terminal-modal-table-wrap">
              <table className="terminal-modal-table">
                <thead><tr><th>DATE</th><th style={{textAlign:'right'}}>VALUE</th></tr></thead>
                <tbody>
                  {detail.observations.slice(-24).reverse().map((o, i) => (
                    <tr key={i}><td>{o.date}</td><td style={{textAlign:'right'}}>{formatValue(o.value)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Larger SVG chart for the detail modal — full history, hover crosshair + readout.
function BigChart({ points, unit }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  if (!points || points.length < 2) return <div style={{padding:20,textAlign:'center',color:'#666'}}>insufficient data</div>;
  const values = points.map(p => p.value);
  const min = Math.min(...values), max = Math.max(...values);
  const range = (max - min) || 1;
  const w = 900, h = 240;
  const xy = points.map((p, i) => ({
    x: (i / (points.length - 1)) * w,
    y: h - ((p.value - min) / range) * (h - 16) - 8,
  }));
  const polyPts = xy.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const last = values[values.length - 1], first = values[0];
  const stroke = last > first ? '#00FF41' : last < first ? '#FF3030' : '#888';
  function onMove(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    const r = (e.clientX - rect.left) / rect.width;
    setHoverIdx(Math.max(0, Math.min(points.length - 1, Math.round(r * (points.length - 1)))));
  }
  const hover = hoverIdx != null ? points[hoverIdx] : null;
  const hoverXY = hoverIdx != null ? xy[hoverIdx] : null;
  const readout = hover ? hover : points[points.length - 1];
  return (
    <div>
      <svg width="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" onMouseMove={onMove} onMouseLeave={() => setHoverIdx(null)} style={{ cursor: 'crosshair', display: 'block' }}>
        {/* grid */}
        {[0.25, 0.5, 0.75].map(f => <line key={f} x1={0} x2={w} y1={f*h} y2={f*h} stroke="#332200" strokeWidth="0.5" strokeDasharray="2 4" vectorEffect="non-scaling-stroke" />)}
        <polyline points={polyPts} fill="none" stroke={stroke} strokeWidth="1.4" vectorEffect="non-scaling-stroke" />
        {hoverXY && <line x1={hoverXY.x} x2={hoverXY.x} y1={0} y2={h} stroke="#FFB000" strokeWidth="0.8" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />}
        {hoverXY && <circle cx={hoverXY.x} cy={hoverXY.y} r="3" fill="#FFB000" />}
        {!hoverXY && <circle cx={xy[xy.length-1].x} cy={xy[xy.length-1].y} r="2.5" fill={stroke} />}
      </svg>
      <div className="terminal-sparkline-readout" style={{marginTop:6}}>
        <span className="tt-label">{hover ? 'HOVER' : 'LATEST'}</span>
        <span className="tt-date">{formatTooltipDate(readout.date)}</span>
        <span className="tt-value">{formatValue(readout.value)}{unit ? ` ${unit}` : ''}</span>
      </div>
    </div>
  );
}

// Inline material calculator — uses /api/toolbox/estimate which talks to the same
// SQL-backed marketData.estimateMaterialCost as the chat tool. Same numbers, no drift.
function CalcWidget({ regions }) {
  const [material, setMaterial] = useState('concrete');
  const [qty, setQty] = useState('');
  const [loc, setLoc] = useState('');
  const [months, setMonths] = useState(6);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const MATERIALS = [
    { id: 'concrete', label: 'Concrete', unit: 'cubic yards' },
    { id: 'lumber',   label: 'Lumber',   unit: 'MBF' },
    { id: 'steel',    label: 'Steel',    unit: 'tons' },
    { id: 'rebar',    label: 'Rebar',    unit: 'tons' },
    { id: 'copper',   label: 'Copper',   unit: 'lb' },
    { id: 'diesel',   label: 'Diesel',   unit: 'gallons' },
    { id: 'asphalt',  label: 'Asphalt',  unit: 'tons' },
    { id: 'cement',   label: 'Cement',   unit: 'tons' },
  ];
  const selected = MATERIALS.find(m => m.id === material) || MATERIALS[0];

  async function run() {
    const q = parseFloat(qty);
    if (!isFinite(q) || q <= 0) return;
    setBusy(true); setResult(null);
    try {
      const res = await fetch('/api/toolbox/estimate', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin', body: JSON.stringify({ material, quantity: q, unit: selected.unit, location: loc || null, monthsAhead: parseInt(months, 10) || 0 }),
      });
      setResult(await res.json());
    } catch (err) { setResult({ error: err.message }); }
    setBusy(false);
  }

  return (
    <div className="terminal-calc">
      <div className="terminal-rail-head">&raquo; CALCULATOR</div>
      <div className="terminal-calc-body">
        <label>MATERIAL</label>
        <select value={material} onChange={e => setMaterial(e.target.value)}>
          {[...MATERIALS].sort((a, b) => a.label.localeCompare(b.label)).map(m => <option key={m.id} value={m.id}>{m.label.toUpperCase()}</option>)}
        </select>

        <label>QUANTITY ({selected.unit.toUpperCase()})</label>
        <input type="number" min="0" step="any" value={qty} onChange={e => setQty(e.target.value)} placeholder="e.g. 1500" />

        <label>LOCATION (optional)</label>
        <select value={loc} onChange={e => setLoc(e.target.value)}>
          <option value="">US AVG</option>
          {regions.map(r => <option key={r.key} value={r.city}>{r.city.toUpperCase()}</option>)}
        </select>

        <label>MONTHS AHEAD</label>
        <select value={months} onChange={e => setMonths(parseInt(e.target.value, 10))}>
          <option value="0">NOW</option>
          <option value="3">+ 3 MO</option>
          <option value="6">+ 6 MO</option>
          <option value="12">+ 12 MO</option>
        </select>

        <button className="terminal-refresh-btn" onClick={run} disabled={busy || !qty}>
          {busy ? 'COMPUTING' : 'ESTIMATE'}
        </button>

        {result && !result.error && (
          <div className="terminal-calc-result">
            <div><span>CURRENT</span><b>${Number(result.currentTotal || 0).toLocaleString()}</b></div>
            <div><span>{months > 0 ? `${months}MO` : 'NOW'}</span><b>${Number(result.forecastTotal || 0).toLocaleString()}</b></div>
            <div><span>Δ</span><b className={changeClass(result.percentChange)}>{formatChange(result.percentChange)}</b></div>
            <div style={{ fontSize: 9, color: '#777', marginTop: 4, lineHeight: 1.3 }}>{result.regionalNote || ''}</div>
          </div>
        )}
        {result && result.error && (
          <div className="terminal-calc-error">{result.error}</div>
        )}
      </div>
    </div>
  );
}

export default function Toolbox() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('ALL');
  const [region, setRegion] = useState('ALL');
  const [search, setSearch] = useState('');
  const [direction, setDirection] = useState('ALL');
  const [refreshing, setRefreshing] = useState(false);
  const [pinned, setPinned] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('bidboard-terminal-pinned') || '[]')); }
    catch { return new Set(); }
  });
  const [detailKey, setDetailKey] = useState(null);
  const [regionsList, setRegionsList] = useState([]);
  const searchRef = useRef(null);
  const clock = useClock();

  // Regions list — used by the calculator's city dropdown, same data as the Terminal filter
  useEffect(() => {
    fetch('/api/toolbox/regions', { credentials: 'same-origin' }).then(r => r.json()).then(setRegionsList).catch(() => {});
  }, []);

  function togglePin(key) {
    setPinned(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem('bidboard-terminal-pinned', JSON.stringify([...next])); } catch {}
      return next;
    });
  }

  // Keep the body class set while this page is mounted so global .app-main gets
  // overflow-x: hidden — prevents any wide inline child from creating a page-level scrollbar.
  useEffect(() => {
    document.body.classList.add('terminal-active');
    return () => document.body.classList.remove('terminal-active');
  }, []);

  useEffect(() => {
    function onKey(e) {
      if (e.key === '/' && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === 'Escape' && document.activeElement === searchRef.current) {
        setSearch('');
        searchRef.current.blur();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/toolbox/series', { credentials: 'same-origin' });
      setData(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Admin-triggered re-ingest. Note: backend does 30+ FRED fetches and can take 30-60s.
  async function refreshAll() {
    setRefreshing(true);
    try {
      await fetch('/api/toolbox/refresh', { method: 'POST', credentials: 'same-origin' });
      await load();
    } finally {
      setRefreshing(false);
    }
  }

  const series = useMemo(() => data?.series || [], [data]);

  const regionOptions = useMemo(() => {
    const all = new Set(series.filter(s => !s.noData).map(s => s.region || 'US National'));
    // Sort with National first, then alphabetical
    return ['ALL', 'US National', ...[...all].filter(r => r !== 'US National').sort()];
  }, [series]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return series.filter(s => {
      if (s.noData) return false;  // omit cards with no real observations — no fake data
      if (category !== 'ALL' && (s.category || '').toUpperCase() !== category) return false;
      if (region !== 'ALL' && (s.region || 'US National') !== region) return false;
      if (direction === 'UP' && !(s.percentChange > 0.05)) return false;
      if (direction === 'DOWN' && !(s.percentChange < -0.05)) return false;
      if (needle) {
        const hay = `${s.displayName || ''} ${s.key || ''} ${s.unit || ''} ${s.description || ''} ${s.region || ''}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [series, category, region, direction, search]);

  const activeFilterCount = (category !== 'ALL' ? 1 : 0) + (region !== 'ALL' ? 1 : 0) + (direction !== 'ALL' ? 1 : 0) + (search.trim() ? 1 : 0);
  const clearFilters = () => { setCategory('ALL'); setRegion('ALL'); setDirection('ALL'); setSearch(''); };

  // Pinned cards float to a WATCHLIST section above the main grid.
  const watchlist = useMemo(() => filtered.filter(s => pinned.has(s.key)), [filtered, pinned]);
  const mainGrid  = useMemo(() => filtered.filter(s => !pinned.has(s.key)), [filtered, pinned]);

  // Ticker only shows series with real data + real % change; fall back to empty
  const tickerItems = series
    .filter(s => !s.noData && isFinite(s.percentChange))
    .map(s => ({
      sym: (s.key || '').toUpperCase().slice(0, 8),
      value: formatValue(s.currentValue, s.unit),
      pct: s.percentChange,
    }));

  const lastIngest = useMemo(() => {
    const times = series.map(s => s.lastFetchedAt).filter(Boolean).map(t => new Date(t).getTime());
    return times.length ? new Date(Math.max(...times)) : null;
  }, [series]);

  const withDataCount = series.filter(s => !s.noData).length;
  const topMovers = useMemo(() => {
    return [...series]
      .filter(s => !s.noData && isFinite(s.percentChange))
      .sort((a, b) => Math.abs(b.percentChange) - Math.abs(a.percentChange))
      .slice(0, 8);
  }, [series]);

  return (
    <div className="terminal-page">
      {/* Top bar */}
      <div className="terminal-top-bar">
        <h2><span className="terminal-live-dot" />CMC MARKET TERMINAL</h2>
        <div>
          <span className="terminal-clock">
            {clock.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase()} &middot; {clock.toLocaleTimeString('en-US', { hour12: false })}
          </span>
          <span style={{ color: '#888' }}>
            LAST INGEST {lastIngest ? lastIngest.toLocaleTimeString('en-US', { hour12: false }) : '—'}
          </span>
          <button className="terminal-refresh-btn" onClick={refreshAll} disabled={refreshing || loading}>
            {refreshing ? 'INGESTING...' : 'REFRESH'}
          </button>
        </div>
      </div>

      {/* Real-data ticker */}
      <div className="terminal-ticker">
        <div className="terminal-ticker-track">
          {tickerItems.length === 0 ? (
            <span className="terminal-ticker-item"><span className="terminal-ticker-sym">—</span> <span className="terminal-flat">NO DATA</span></span>
          ) : [...tickerItems, ...tickerItems].map((t, i) => (
            <span key={i} className="terminal-ticker-item">
              <span className="terminal-ticker-sym">{t.sym}</span>
              <span className="terminal-ticker-value">{t.value}</span>
              <span className={changeClass(t.pct)}>{formatChange(t.pct)}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="terminal-search-bar">
        <span className="terminal-search-prompt">&gt;</span>
        <input
          ref={searchRef}
          type="text"
          className="terminal-search-input"
          placeholder="SEARCH SERIES, KEYWORDS, UNITS... (PRESS / TO FOCUS, ESC TO CLEAR)"
          value={search}
          onChange={e => setSearch(e.target.value)}
          spellCheck={false}
          autoComplete="off"
        />
        {search && <button className="terminal-search-clear" onClick={() => setSearch('')} title="Clear (Esc)">×</button>}
      </div>

      {/* Filters */}
      <div className="terminal-filters">
        {CATEGORY_TABS.map(tab => (
          <button key={tab} className={`terminal-filter ${category === tab ? 'active' : ''}`} onClick={() => setCategory(tab)}>
            {tab}
          </button>
        ))}
        <div className="terminal-filter-divider" />
        {['ALL', 'UP', 'DOWN'].map(d => (
          <button key={d} className={`terminal-filter terminal-filter-dir-${d.toLowerCase()} ${direction === d ? 'active' : ''}`} onClick={() => setDirection(d)}>
            {d === 'UP' ? '▲ UP' : d === 'DOWN' ? '▼ DOWN' : 'ALL'}
          </button>
        ))}
        <div className="terminal-filter-spacer" />
        {activeFilterCount > 0 && (
          <button className="terminal-filter terminal-clear-filters" onClick={clearFilters}>
            × CLEAR {activeFilterCount}
          </button>
        )}
        <select
          className="terminal-location-select"
          value={region}
          onChange={e => setRegion(e.target.value)}
          title="Filter by region. Most FRED data is US national; metro/state series come from Case-Shiller and FHFA."
        >
          {regionOptions.map(r => (
            <option key={r} value={r}>REG :: {r === 'ALL' ? 'ALL REGIONS' : r.toUpperCase()}</option>
          ))}
        </select>
      </div>

      {/* Main layout */}
      <div className="terminal-layout">
        <div className="terminal-main-col">
          <div className="terminal-section-header" title="Chart = last 3 years of real data. Forecast = 24-month monthly regression projected 6 months forward.">
            &raquo; {filtered.length} / {withDataCount} SERIES &middot; SRC :: FRED &middot; 3YR CHART &middot; 6MO PROJECTION
          </div>
          {loading && !data ? (
            <div className="terminal-loading">:: LOADING MARKET DATA ::</div>
          ) : filtered.length === 0 ? (
            <div className="terminal-empty">NO MATCHING SERIES</div>
          ) : (
            <>
              {watchlist.length > 0 && (
                <>
                  <div className="terminal-section-header terminal-watchlist-header" title="Your pinned series. Click any card's ★ to add or remove from watchlist. Persisted in your browser.">
                    &raquo; ★ MY WATCHLIST &middot; {watchlist.length} SERIES
                  </div>
                  <div className="terminal-grid">
                    {watchlist.map(s => <TerminalCard key={s.seriesId} s={s} pinned={true} onTogglePin={togglePin} onOpen={setDetailKey} />)}
                  </div>
                </>
              )}
              {mainGrid.length > 0 && (
                <>
                  {watchlist.length > 0 && (
                    <div className="terminal-section-header" style={{ marginTop: 4 }}>&raquo; ALL SERIES</div>
                  )}
                  <div className="terminal-grid">
                    {mainGrid.map(s => <TerminalCard key={s.seriesId} s={s} pinned={false} onTogglePin={togglePin} onOpen={setDetailKey} />)}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Right rail — calculator + system status + top movers */}
        <div className="terminal-rail">
          <CalcWidget regions={regionsList} />
          <div className="terminal-rail-head">&raquo; SYSTEM STATUS</div>
          <div className="terminal-rail-item">
            <span className="terminal-rail-item-tag">SERIES</span>
            <b>{withDataCount}</b> active with data
          </div>
          <div className="terminal-rail-item">
            <span className="terminal-rail-item-tag">LAST</span>
            {lastIngest ? lastIngest.toLocaleString() : 'not yet ingested'}
          </div>
          <div className="terminal-rail-item">
            <span className="terminal-rail-item-tag">SRC</span>
            FRED public CSV — no auth required
          </div>

          <div className="terminal-rail-head">&raquo; TOP MOVERS (6MO PROJECTED)</div>
          {topMovers.length === 0 ? (
            <div className="terminal-rail-item">—</div>
          ) : topMovers.map((m, i) => (
            <div key={i} className="terminal-rail-item">
              <span className="terminal-rail-item-tag">{m.key}</span>
              <span className={changeClass(m.percentChange)}>{formatChange(m.percentChange)}</span>
              <div style={{ color: '#888', fontSize: '10px', marginTop: '2px' }}>{m.displayName}</div>
            </div>
          ))}

          <div className="terminal-rail-head">&raquo; NEWS FEED</div>
          <div className="terminal-rail-item" style={{ color: '#666', fontStyle: 'italic' }}>
            Not yet wired. Real feed (ENR / Construction Dive) pending.
          </div>
        </div>
      </div>

      <div className="terminal-footer">
        <span>
          CMC MARKET TERMINAL v0.3 &middot; FRED PUBLIC CSV &middot; AUTO-INGEST 24H &middot; {series.filter(s => s.noData).length} SERIES PENDING DATA
        </span>
        <span>{filtered.length} OF {withDataCount} SHOWING</span>
      </div>

      {detailKey && <DetailModal seriesKey={detailKey} onClose={() => setDetailKey(null)} />}
    </div>
  );
}
